import { DOCUMENT } from '@angular/common';
import {
    AfterViewInit,
    Component,
    DestroyRef,
    ElementRef,
    inject,
    input,
    ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Params, Router, UrlSegmentGroup, UrlTree } from '@angular/router';
import { SiteStatusEnum } from '@app/enums/site-status.enum';
import { PatientEditRequestService } from '@app/v2/services/patient-edit-request/patient-edit-request.service';
import { RequestService } from '@app/v2/services/request/request.service';
import { SiteService } from '@app/v2/services/site/site.service';
import { UserServiceV2 } from '@app/v2/services/user-service/user.service';
import type {
    GlobalSidenavOptions,
    MdsAppbarActionItems,
    MdsAppbarProfileInformation,
} from '@medpacesoftwaredevelopment/designsystem';
import { AuthService } from '@services/auth/auth.service';
import { EnvConfigurationService } from '@services/env/env-configuration.service';
import { GroupService } from '@services/group/group.service';
import { LoadingViewTriggerService } from '@services/loading-module-trigger/loading-module-trigger.service';
import { UserService } from '@services/user/user.service';
import { combineLatest, distinctUntilChanged, filter, map, Observable, shareReplay, startWith, switchMap } from 'rxjs';
import { AccessRequestService } from 'src/app/v2/services/access-request/access-request.service';
export interface NotificationItems {
    link: string;
    queryParams?: Params | null | undefined;
    color?: string;
    content: string;
    portal: string;
}

interface SidenavAction extends GlobalSidenavOptions {
    permissions?: string[];
    children?: (GlobalSidenavOptions & { permissions: string[] })[];
}
@Component({
    selector: 'medpace-appbar',
    templateUrl: './appbar.component.html',
    styleUrls: ['./appbar.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false,
})
export class AppbarComponent implements AfterViewInit {
    // no idea what this is for but it was too difficult to copy it from app.component.ts directly, so I made it an input
    isRouterOutputLoad = input.required<boolean>();
    displaySideBar = input<boolean>(false);
    private readonly document = inject(DOCUMENT);
    private readonly elementRef = inject(ElementRef);
    private readonly destroyRef = inject(DestroyRef);
    userService = inject(UserService);
    authService = inject(AuthService);
    router = inject(Router);
    accessRequestServicev2 = inject(AccessRequestService);
    loadingService = inject(LoadingViewTriggerService);
    groupService = inject(GroupService);

    siteService = inject(SiteService);
    patientEditRequestService = inject(PatientEditRequestService);
    requestService = inject(RequestService);
    envConfigurationService = inject(EnvConfigurationService);
    applicationName = this.envConfigurationService.configuration.applicationName;
    private readonly closedSidebarPaths = ['/set'];

    isSidebarOpenOnPageLoad$ = this.router.events.pipe(
        filter((event) => event instanceof NavigationEnd),
        map((event) => {
            const urlTree: UrlTree = this.router.parseUrl(event.urlAfterRedirects);
            const segmentGroup: UrlSegmentGroup = urlTree.root.children['primary'];
            const segments = segmentGroup?.segments.map((s) => s.path) || [];

            const isSidebarClosed = this.closedSidebarPaths.some((basePath) => {
                const baseSegments = basePath.split('/').filter(Boolean);
                return segments.length >= baseSegments.length && baseSegments.every((seg, i) => seg === segments[i]);
            });

            return !isSidebarClosed;
        }),
        startWith(true)
    );

    isLoading$ = this.loadingService.observeLoading().pipe(takeUntilDestroyed());
    userServiceV2 = inject(UserServiceV2);
    sidenavActions: SidenavAction[] = [
        {
            name: 'Dashboard',
            routerLink: '/dashboard',
            icon: '',
            permissions: ['dashboard.page.view'],
        },
        {
            name: 'Approvals',
            icon: '',
            children: [
                {
                    name: 'Invites',
                    routerLink: '/invitation-approvals',
                    icon: '',
                    permissions: ['invitation-approvals.page.view'],
                },
                {
                    name: 'Payments',
                    routerLink: '/payment-approvals',
                    icon: '',
                    permissions: ['payment-approvals.page.view'],
                },
            ],
        },
        {
            name: 'Studies',
            routerLink: '/studies',
            icon: '',
            permissions: ['studies.page.view'],
        },
        {
            name: 'Sites',
            routerLink: '/sites',
            icon: '',
            permissions: ['sites.page.view'],
        },
        {
            name: 'Patients',
            routerLink: '/patients',
            icon: '',
            permissions: ['patients.page.view'],
        },
        {
            name: 'Requests',
            routerLink: '/requests',
            icon: '',
            permissions: ['requests.page.view'],
        },
        {
            name: 'Manage',
            routerLink: '/manage',
            icon: '',
            permissions: ['manage.page.view'],
        },
    ];

    actionItems: MdsAppbarActionItems[] = [];

    private readonly emptyProfile: MdsAppbarProfileInformation = {
        name: '',
        initial: '',
        link: '',
    };

    isAuth$ = this.authService.getIsAuthenticated().pipe(distinctUntilChanged(), shareReplay(1));

    hasAccount$ = this.isAuth$.pipe(
        filter(Boolean),
        switchMap((_) => this.userServiceV2.hasAccount.get$())
    );

    sidenavActions$: Observable<GlobalSidenavOptions[]> = this.hasAccount$.pipe(
        filter(Boolean),
        switchMap((_) => {
            return combineLatest(
                this.sidenavActions.map((option) => {
                    if (option.children?.length) {
                        return combineLatest(
                            option.children.map((child) =>
                                this.userServiceV2
                                    .hasAllPermissions$(child.permissions)
                                    .pipe(map((permitted) => (permitted ? child : null)))
                            )
                        ).pipe(
                            map((children) => {
                                const permitted = children.filter((c) => c !== null);
                                if (permitted.length === 0) return null;
                                return { ...option, children: permitted } as GlobalSidenavOptions;
                            })
                        );
                    }
                    return this.userServiceV2.hasAllPermissions$(option.permissions).pipe(
                        map((permitted) => {
                            if (!permitted) return null;
                            return option;
                        })
                    );
                })
            ).pipe(map((options) => options.filter((opt) => opt !== null)));
        })
    );

    profile$ = this.hasAccount$.pipe(
        filter(Boolean),
        switchMap((_) =>
            this.userServiceV2.currentUserData.get$().pipe(
                map((user) => {
                    const profileInfo: MdsAppbarProfileInformation = {
                        name: user.fullName,
                        secondaryUserInformation: user.emailAddress,
                        initial: `${user.firstName.at(0)}${user.lastName.at(0)}`,
                        link: 'account',
                    };
                    return profileInfo;
                }),
                startWith(this.emptyProfile)
            )
        )
    );

    permissionMap: Record<string, () => Observable<NotificationItems>> = {
        //has only managers
        'notification.user-access-request.view': () =>
            this.accessRequestServicev2.countPendingAccessRequests.get$().pipe(
                map((accessRequestsCount) => {
                    if (accessRequestsCount <= 0) return null;

                    return <NotificationItems>{
                        link: 'manage/pending-access-requests',
                        color: 'blue',
                        content: 'Pending Access Requests: ' + accessRequestsCount,
                        portal: 'Access Requests',
                    };
                })
            ),
        //all(Managers.Lead Coordinators,Coordinators,Assistants,CRCs)
        'notification.requests-submitted.view': () =>
            this.requestService.submittedRequestsCount.get$().pipe(
                map((submittedRequestsCount) => {
                    if (submittedRequestsCount <= 0) return null;

                    return <NotificationItems>{
                        link: '/requests/notification',
                        color: 'red',
                        content: 'Requests submitted: ' + submittedRequestsCount,
                        portal: 'Requests',
                    };
                })
            ),
        //Managers
        'notification.submitted-patient-edit-request.view': () =>
            // no .get$() here because I had to rework PatientEditRequestService to use signals instead of observables
            this.patientEditRequestService.submittedPatientEditRequests$.pipe(
                map((patientEditRequestCount) => {
                    if (patientEditRequestCount <= 0) return null;

                    return <NotificationItems>{
                        link: '/patients',
                        queryParams: { 'expand-edit-requests': true },
                        color: 'var(--indigo20)',
                        content: 'Edit requests submitted: ' + patientEditRequestCount,
                        portal: 'Patients',
                    };
                })
            ),
        //CRCs
        'notification.preregistered-sites.view': () =>
            this.siteService.numberOfSitesInStatus.get$(SiteStatusEnum.PreRegistered).pipe(
                map((sitesCount) => {
                    if (sitesCount == 0) return null;

                    return <NotificationItems>{
                        link: 'dashboard/notification',
                        color: 'blue',
                        content: 'Sites awaiting registration: ' + sitesCount,
                        portal: 'Dashboard',
                    };
                })
            ),
    };

    notifications$ = this.hasAccount$.pipe(
        filter(Boolean),
        switchMap((_) =>
            this.userServiceV2.currentUserData.get$().pipe(
                map((userData) => userData.userPermissions.map((p) => this.permissionMap[p]).filter(Boolean)),
                switchMap((notificationsObservables) => combineLatest(notificationsObservables.map((func) => func()))),
                map((notifications) => notifications.filter(Boolean))
            )
        )
    );

    logout() {
        this.authService.logout();
    }

    ngAfterViewInit(): void {
        const appbarCont = this.elementRef.nativeElement.querySelector('.appbar-cont');
        if (!appbarCont) return;
        if (typeof ResizeObserver === 'undefined') return;

        const observer = new ResizeObserver((entries) => {
            const height = entries[0]?.contentRect.height;
            if (height !== undefined) {
                this.document.documentElement.style.setProperty('--appbar-height', `${height}px`);
            }
        });

        observer.observe(appbarCont);
        this.destroyRef.onDestroy(() => observer.disconnect());
    }

    //when clicking toggle the navOpened flag is changed to true and footer margin is set to declared in variable , clicking one more time on hamburger setting footer style to empty
    navOpened = false;
    resizeFooter() {
        let footer = document.getElementById('footer');
        this.navOpened = !this.navOpened;

        this.navOpened ? (footer.style.marginLeft = 'var(--mat-sidenav-container-width, 15rem)') : (footer.style = '');
    }
}
